/**
 * Exports API - Generate and download export packages.
 */

import apiClient, { type RequestConfig } from './client';
import type {
  Export,
  CreateExportRequest,
  ExportDestinationProfile,
  ExportPreflight,
  PaginatedResponse,
  PaginationParams,
} from './types';

const ENDPOINT = '/exports';

/** Verification receipt payload */
export interface ReceiptPayload {
  receipt_version: string;
  export_id: string;
  matter_id: string;
  format: string;
  document_count: number;
  export_sha256: string | null;
  manifest_sha256: string | null;
  created_at: string;
  completed_at: string | null;
  generated_at: string;
  generated_by: string;
}

/** Signed verification receipt */
export interface VerificationReceipt {
  payload: ReceiptPayload;
  hmac_signature: string;
  algorithm: string;
  verification_instructions: string;
}

/** Result of receipt verification */
export interface ReceiptVerifyResult {
  status: 'MATCH' | 'TAMPERED' | 'EXPORT_MISMATCH';
  export_id: string;
  checked_at: string;
  details: string | null;
}

/** Export hash manifest */
export interface ExportManifest {
  export_id: string;
  export_sha256: string | null;
  manifest_sha256: string | null;
  verification_status: string | null;
  document_count: number;
  manifest: Record<string, string> | null;
}

/** Export verify result */
export interface ExportVerifyResult {
  export_id: string;
  is_valid: boolean;
  export_sha256: string | null;
  verified_at: string | null;
  errors: string[];
}

export const exportsApi = {
  /**
   * Create a new export.
   */
  async create(
    matterId: string,
    request: CreateExportRequest,
    config?: RequestConfig
  ): Promise<Export> {
    const { data } = await apiClient.post<Export>(
      ENDPOINT,
      { ...request, matter_id: matterId },
      config
    );
    return data;
  },

  /**
   * Get an export by ID.
   */
  async get(id: string, config?: RequestConfig): Promise<Export> {
    const { data } = await apiClient.get<Export>(`${ENDPOINT}/${id}`, config);
    return data;
  },

  /**
   * List exports for a matter.
   */
  async listForMatter(
    matterId: string,
    params?: PaginationParams & {
      status?: string;
      format?: string;
    },
    config?: RequestConfig
  ): Promise<PaginatedResponse<Export>> {
    const { data } = await apiClient.get<PaginatedResponse<Export> | Export[]>(
      ENDPOINT,
      {
        ...config,
        params: { matter_id: matterId, ...params } as Record<string, string | number | boolean | undefined>,
      }
    );
    if (Array.isArray(data)) {
      return {
        items: data.map((item) => ({
          ...item,
          id: item.id || (item as Export & { export_id?: string }).export_id || "",
        })),
        total: data.length,
        page: 1,
        page_size: data.length,
        pages: 1,
      };
    }
    return data;
  },

  /**
   * Cancel a pending/processing export.
   */
  async cancel(id: string, config?: RequestConfig): Promise<Export> {
    const { data } = await apiClient.post<Export>(`${ENDPOINT}/${id}/cancel`, null, config);
    return data;
  },

  /**
   * Retry a failed export.
   */
  async retry(id: string, config?: RequestConfig): Promise<{
    export_id: string;
    job_id: string;
    status: string;
  }> {
    const { data } = await apiClient.post<{
      export_id: string;
      job_id: string;
      status: string;
    }>(`${ENDPOINT}/${id}/retry`, null, config);
    return data;
  },

  /**
   * Delete an export.
   */
  async delete(id: string, config?: RequestConfig): Promise<void> {
    await apiClient.delete(`${ENDPOINT}/${id}`, config);
  },

  /**
   * Create a load file export.
   */
  async createLoadFileExport(
    matterId: string,
    request: Record<string, unknown>,
    config?: RequestConfig
  ): Promise<Export> {
    const { data } = await apiClient.post<Export>(
      `/matters/${matterId}/load-file-exports`,
      request,
      config
    );
    return data;
  },

  /**
   * Export privilege log.
   */
  async exportPrivilegeLog(
    matterId: string,
    config?: RequestConfig
  ): Promise<Blob> {
    const { data } = await apiClient.post<Blob>(
      `/matters/${matterId}/privilege-log-export`,
      {},
      { ...config }
    );
    return data;
  },

  /**
   * Get download URL for a completed export.
   */
  async getDownloadUrl(id: string, config?: RequestConfig): Promise<{
    url: string;
    download_url?: string;
    expires_at: string | null;
    filename: string | null;
    size: number;
  }> {
    const { data } = await apiClient.get<{
      url?: string;
      download_url?: string;
      expires_at: string | null;
      filename: string | null;
      size?: number;
    }>(`${ENDPOINT}/${id}/download-url`, config);
    return {
      url: data.url || data.download_url || `${ENDPOINT}/${id}/download`,
      download_url: data.download_url,
      expires_at: data.expires_at,
      filename: data.filename,
      size: data.size || 0,
    };
  },

  async listDestinations(config?: RequestConfig): Promise<ExportDestinationProfile[]> {
    const { data } = await apiClient.get<ExportDestinationProfile[]>(`${ENDPOINT}/destinations`, config);
    return data;
  },

  async preflight(
    request: {
      matter_id: string;
      format: string;
      include_privileged?: boolean;
      include_natives?: boolean;
      include_text?: boolean;
      include_images?: boolean;
      relevance_threshold?: number;
    },
    config?: RequestConfig
  ): Promise<ExportPreflight> {
    const { data } = await apiClient.post<ExportPreflight>(`${ENDPOINT}/preflight`, request, config);
    return data;
  },

  /**
   * Download an export directly.
   */
  async download(id: string, config?: RequestConfig): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(`${ENDPOINT}/${id}/download`, config);
    return data;
  },

  /**
   * Get export preview (sample of what will be exported).
   */
  async getPreview(
    matterId: string,
    options: {
      document_ids?: string[];
      include_all?: boolean;
      limit?: number;
    },
    config?: RequestConfig
  ): Promise<{
    sample_documents: Array<{
      id: string;
      filename: string;
      file_size: number;
    }>;
    total_documents: number;
    estimated_size: number;
    estimated_time_seconds: number;
  }> {
    const { data } = await apiClient.post<{
      sample_documents: Array<{
        id: string;
        filename: string;
        file_size: number;
      }>;
      total_documents: number;
      estimated_size: number;
      estimated_time_seconds: number;
    }>(`${ENDPOINT}/preview`, { matter_id: matterId, ...options }, config);
    return data;
  },

  /**
   * Poll for export completion.
   */
  async waitForCompletion(
    id: string,
    onProgress?: (exportData: Export) => void,
    pollInterval = 2000,
    timeout = 600000 // 10 minutes
  ): Promise<Export> {
    const startTime = Date.now();

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const exportData = await this.get(id);

      if (onProgress) {
        onProgress(exportData);
      }

      if (exportData.status === 'completed' || exportData.status === 'failed') {
        return exportData;
      }

      if (Date.now() - startTime > timeout) {
        throw new Error('Export timed out');
      }

      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }
  },

  /**
   * Generate a signed verification receipt for a completed export.
   */
  async generateReceipt(id: string, config?: RequestConfig): Promise<VerificationReceipt> {
    const { data } = await apiClient.post<VerificationReceipt>(
      `${ENDPOINT}/${id}/receipt`,
      null,
      config
    );
    return data;
  },

  /**
   * Verify a previously-issued receipt.
   */
  async verifyReceipt(
    receipt: VerificationReceipt,
    config?: RequestConfig
  ): Promise<ReceiptVerifyResult> {
    const { data } = await apiClient.post<ReceiptVerifyResult>(
      `${ENDPOINT}/verify-receipt`,
      { payload: receipt.payload, hmac_signature: receipt.hmac_signature },
      config
    );
    return data;
  },

  /**
   * Get the hash manifest for an export.
   */
  async getManifest(id: string, config?: RequestConfig): Promise<ExportManifest> {
    const { data } = await apiClient.get<ExportManifest>(
      `${ENDPOINT}/${id}/manifest`,
      config
    );
    return data;
  },

  /**
   * Verify an export (re-calculate SHA256 and check manifest).
   */
  async verify(id: string, config?: RequestConfig): Promise<ExportVerifyResult> {
    const { data } = await apiClient.post<ExportVerifyResult>(
      `${ENDPOINT}/${id}/verify`,
      null,
      config
    );
    return data;
  },

  /**
   * Subscribe to export updates via SSE.
   */
  subscribeToExport(
    id: string,
    handlers: {
      onProgress?: (progress: { percent: number; message: string }) => void;
      onComplete?: (exportData: Export) => void;
      onError?: (error: Event) => void;
    }
  ): EventSource {
    const eventSource = apiClient.sse(`${ENDPOINT}/${id}/stream`, {
      onMessage: (event) => {
        const data = JSON.parse(event.data);
        switch (data.type) {
          case 'progress':
            handlers.onProgress?.(data.payload);
            break;
          case 'complete':
            handlers.onComplete?.(data.export);
            break;
        }
      },
      onError: handlers.onError,
    });
    return eventSource;
  },
};

export default exportsApi;
